// apps/web/src/pages/brand/Competitors.tsx
//
// Competitor research: watched public Instagram accounts (Zernio business
// discovery), a comparison table vs our own account, and competitors' top
// recent posts. Snapshots refresh daily (worker "competitors" cron).

import { useCallback, useMemo, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ExternalLink, Eye, RefreshCw, Trash2, AlertCircle } from "lucide-react";
import { useBrandContext } from "@/contexts/brand-context";
import { usePermissions } from "@/hooks/use-permissions";
import { useLocale, formatNumber, formatDate } from "@/i18n/format";
import {
  useCompetitorInsights,
  useAddCompetitor,
  useRemoveCompetitor,
  useRefreshCompetitors,
  useUpdateCompetitorNotes,
  type CompetitorRow,
  type CompetitorPost,
  type CompetitorPostFormat,
} from "@/hooks/use-competitors";
import { TimeRangeSelector, type AnalyticsRange } from "@/components/analytics/time-range-selector";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/ui/empty-state";
import { cn } from "@/lib/utils";

const FORMATS: CompetitorPostFormat[] = ["REEL", "CAROUSEL", "IMAGE", "VIDEO"];
const FORMAT_COLORS: Record<CompetitorPostFormat, string> = {
  REEL: "bg-primary",
  CAROUSEL: "bg-sky-500",
  IMAGE: "bg-amber-500",
  VIDEO: "bg-emerald-500",
};
const DAYS_VALUES: AnalyticsRange[] = [7, 30, 90];

function parseDays(raw: string | null): AnalyticsRange {
  const n = Number(raw);
  return (DAYS_VALUES as number[]).includes(n) ? (n as AnalyticsRange) : 30;
}

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

function FormatMixBar({ row }: { row: CompetitorRow }) {
  const { t } = useTranslation("competitors");
  const share = row.insights?.formatShare;
  if (!share || !row.insights?.postsInWindow) return <span className="text-muted-foreground">—</span>;
  const parts = FORMATS.filter((f) => share[f] > 0);
  return (
    <div className="min-w-[140px] space-y-1">
      <div className="flex h-2 w-full overflow-hidden rounded bg-muted">
        {parts.map((f) => (
          <div key={f} className={FORMAT_COLORS[f]} style={{ width: `${share[f] * 100}%` }} />
        ))}
      </div>
      <div className="text-xs text-muted-foreground">
        {parts.map((f) => `${t(`formats.${f}`)} ${Math.round(share[f] * 100)}%`).join(" · ")}
      </div>
    </div>
  );
}

function NoteCell({ row, brandId, canEdit }: { row: CompetitorRow; brandId: string; canEdit: boolean }) {
  const { t } = useTranslation("competitors");
  const update = useUpdateCompetitorNotes(brandId);
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(row.notes ?? "");

  if (row.isOwn) return null;
  if (!editing) {
    if (!row.notes && !canEdit) return null;
    return (
      <button
        type="button"
        disabled={!canEdit}
        onClick={() => {
          setValue(row.notes ?? "");
          setEditing(true);
        }}
        className="block text-left text-xs text-muted-foreground hover:text-foreground disabled:hover:text-muted-foreground"
      >
        {row.notes || t("table.addNote")}
      </button>
    );
  }
  const save = async () => {
    setEditing(false);
    const next = value.trim() || null;
    if (next === (row.notes ?? null)) return;
    try {
      await update.mutateAsync({ id: row.id, notes: next });
      toast.success(t("table.noteSaved"));
    } catch (err) {
      toast.error(errorMessage(err, t("errors.generic")));
    }
  };
  return (
    <Input
      autoFocus
      value={value}
      placeholder={t("table.notePlaceholder")}
      maxLength={1000}
      onChange={(e) => setValue(e.target.value)}
      onBlur={save}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
        if (e.key === "Escape") setEditing(false);
      }}
      className="mt-1 h-7 text-xs"
    />
  );
}

function ComparisonTable({
  rows,
  days,
  brandId,
  canEdit,
}: {
  rows: CompetitorRow[];
  days: number;
  brandId: string;
  canEdit: boolean;
}) {
  const { t } = useTranslation("competitors");
  const locale = useLocale();
  const remove = useRemoveCompetitor(brandId);
  const num = (n: number | null | undefined, opts?: Intl.NumberFormatOptions) =>
    n == null ? "—" : formatNumber(n, locale, opts);

  const onRemove = async (row: CompetitorRow) => {
    try {
      await remove.mutateAsync(row.id);
      toast.success(t("table.removed", { username: row.username }));
    } catch (err) {
      toast.error(errorMessage(err, t("errors.generic")));
    }
  };

  return (
    <Card className="p-4 space-y-3">
      <div>
        <div className="text-sm font-medium">{t("table.title")}</div>
        <p className="text-xs text-muted-foreground">{t("table.subtitle", { days })}</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
              <th className="py-2 pr-3 font-normal">{t("table.account")}</th>
              <th className="py-2 pr-3 font-normal">{t("table.followers")}</th>
              <th className="py-2 pr-3 font-normal">{t("table.postsPerWeek")}</th>
              <th className="py-2 pr-3 font-normal">{t("table.formatMix")}</th>
              <th className="py-2 pr-3 font-normal">{t("table.medianEngagement")}</th>
              <th className="py-2 pr-3 font-normal">{t("table.engagementRate")}</th>
              <th className="py-2 pr-3 font-normal">{t("table.updated")}</th>
              <th className="py-2 font-normal" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} className={cn("border-t border-border align-top", row.isOwn && "bg-muted/40")}>
                <td className="py-2 pr-3">
                  <div className="flex items-center gap-2">
                    <a
                      href={`https://www.instagram.com/${row.username}/`}
                      target="_blank"
                      rel="noreferrer"
                      className="font-medium hover:underline"
                    >
                      @{row.username}
                    </a>
                    {row.isOwn && <Badge variant="secondary">{t("table.you")}</Badge>}
                  </div>
                  {row.displayName && <div className="text-xs text-muted-foreground">{row.displayName}</div>}
                  <NoteCell row={row} brandId={brandId} canEdit={canEdit} />
                </td>
                <td className="py-2 pr-3 tabular-nums whitespace-nowrap">
                  {num(row.followersCount)}
                  {row.followersDelta != null && row.followersDelta !== 0 && (
                    <span
                      className={cn(
                        "ml-1 text-xs",
                        row.followersDelta > 0 ? "text-emerald-600" : "text-destructive",
                      )}
                    >
                      {row.followersDelta > 0 ? "+" : ""}
                      {num(row.followersDelta)}
                    </span>
                  )}
                </td>
                <td className="py-2 pr-3 tabular-nums">
                  {row.insights ? (
                    <span title={row.insights.windowTruncated ? t("table.lowerBound") : undefined}>
                      {row.insights.windowTruncated ? "≥ " : ""}
                      {num(row.insights.postsPerWeek, { maximumFractionDigits: 1 })}
                    </span>
                  ) : (
                    "—"
                  )}
                </td>
                <td className="py-2 pr-3">
                  <FormatMixBar row={row} />
                </td>
                <td className="py-2 pr-3 tabular-nums">
                  {num(row.insights?.medianEngagement, { maximumFractionDigits: 0 })}
                </td>
                <td className="py-2 pr-3 tabular-nums">
                  {num(row.insights?.engagementRate, { style: "percent", maximumFractionDigits: 2 })}
                </td>
                <td className="py-2 pr-3 text-xs text-muted-foreground whitespace-nowrap">
                  {row.lastRefreshedAt
                    ? formatDate(row.lastRefreshedAt, locale, { month: "short", day: "numeric" })
                    : t("table.never")}
                  {row.lastRefreshError && (
                    <span title={t("table.refreshError", { error: row.lastRefreshError })}>
                      <AlertCircle className="ml-1 inline h-3.5 w-3.5 text-destructive" />
                    </span>
                  )}
                </td>
                <td className="py-2 text-right">
                  {canEdit && !row.isOwn && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      aria-label={t("table.remove", { username: row.username })}
                      title={t("table.remove", { username: row.username })}
                      disabled={remove.isPending}
                      onClick={() => onRemove(row)}
                    >
                      <Trash2 />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!rows.some((r) => r.isOwn) && <p className="text-xs text-muted-foreground">{t("table.noOwn")}</p>}
    </Card>
  );
}

function TopPosts({ rows, days }: { rows: CompetitorRow[]; days: number }) {
  const { t } = useTranslation("competitors");
  const locale = useLocale();
  const posts = useMemo(() => {
    const all: Array<CompetitorPost & { username: string }> = [];
    for (const r of rows) for (const p of r.insights?.topPosts ?? []) all.push({ ...p, username: r.username });
    return all.sort((a, b) => b.engagement - a.engagement).slice(0, 12);
  }, [rows]);

  return (
    <Card className="p-4 space-y-3">
      <div>
        <div className="text-sm font-medium">{t("topPosts.title")}</div>
        <p className="text-xs text-muted-foreground">{t("topPosts.subtitle", { days })}</p>
      </div>
      {posts.length === 0 ? (
        <div className="py-6 text-center text-sm text-muted-foreground">{t("topPosts.empty")}</div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {posts.map((p) => (
            <div key={`${p.username}-${p.id}`} className="flex flex-col gap-2 rounded-md border border-border p-3">
              <div className="flex items-center justify-between gap-2 text-xs">
                <span className="font-medium">@{p.username}</span>
                <Badge variant="outline">{t(`formats.${p.format}`)}</Badge>
              </div>
              <p className="line-clamp-3 text-sm text-muted-foreground">{p.caption || "—"}</p>
              <div className="mt-auto flex items-center justify-between gap-2 text-xs text-muted-foreground">
                <span className="tabular-nums">
                  {p.likeCount == null
                    ? t("topPosts.likesHidden")
                    : t("topPosts.likes", { count: p.likeCount })}
                  {" · "}
                  {t("topPosts.comments", { count: p.commentsCount ?? 0 })}
                  {p.timestamp && ` · ${formatDate(p.timestamp, locale, { month: "short", day: "numeric" })}`}
                </span>
                {p.permalink && (
                  <a
                    href={p.permalink}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 whitespace-nowrap text-primary hover:underline"
                  >
                    {t("topPosts.open")}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function AddCompetitorForm({ brandId }: { brandId: string }) {
  const { t } = useTranslation("competitors");
  const add = useAddCompetitor(brandId);
  const [username, setUsername] = useState("");
  const [notes, setNotes] = useState("");

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!username.trim()) return;
    try {
      const created = await add.mutateAsync({ username: username.trim(), notes: notes.trim() || undefined });
      toast.success(t("add.added", { username: created.username }));
      setUsername("");
      setNotes("");
    } catch (err) {
      toast.error(errorMessage(err, t("errors.generic")));
    }
  };

  return (
    <Card className="p-4">
      <form onSubmit={onSubmit} className="flex flex-col gap-2 sm:flex-row sm:items-end">
        <label className="flex-1 space-y-1 text-xs font-medium">
          <span>{t("add.username")}</span>
          <Input
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder={t("add.usernamePlaceholder")}
            autoComplete="off"
          />
        </label>
        <label className="flex-1 space-y-1 text-xs font-medium">
          <span>{t("add.notes")}</span>
          <Input
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t("add.notesPlaceholder")}
            maxLength={1000}
          />
        </label>
        <Button type="submit" disabled={add.isPending || !username.trim()}>
          {add.isPending ? t("add.adding") : t("add.submit")}
        </Button>
      </form>
      <p className="mt-2 text-xs text-muted-foreground">{t("add.hint")}</p>
    </Card>
  );
}

export function BrandCompetitorsPage() {
  const { t } = useTranslation("competitors");
  const { brand } = useBrandContext();
  const { canEdit } = usePermissions();
  const brandId = brand?.id ?? "";
  const [searchParams, setSearchParams] = useSearchParams();
  const days = parseDays(searchParams.get("days"));
  const setDays = useCallback(
    (v: AnalyticsRange) =>
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (v === 30) next.delete("days");
          else next.set("days", String(v));
          return next;
        },
        { replace: true },
      ),
    [setSearchParams],
  );

  const { data, isLoading, isError, error } = useCompetitorInsights(brandId, days);
  const refresh = useRefreshCompetitors(brandId);
  const rows = useMemo(() => (data ? [...(data.own ? [data.own] : []), ...data.competitors] : []), [data]);

  const onRefresh = async () => {
    try {
      await refresh.mutateAsync();
      toast.success(t("refreshQueued"));
    } catch (err) {
      toast.error(errorMessage(err, t("errors.generic")));
    }
  };

  return (
    <div className="w-full space-y-6 px-6 py-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">{t("title")}</h1>
          <p className="text-sm text-muted-foreground">{t("subtitle")}</p>
        </div>
        <div className="flex items-center gap-2">
          <TimeRangeSelector value={days} onChange={setDays} />
          {canEdit && (data?.competitors.length ?? 0) > 0 && (
            <Button variant="outline" size="sm" onClick={onRefresh} disabled={refresh.isPending}>
              <RefreshCw className={cn(refresh.isPending && "animate-spin")} />
              {t("refresh")}
            </Button>
          )}
        </div>
      </div>

      {canEdit && <AddCompetitorForm brandId={brandId} />}

      {isLoading ? (
        <div className="space-y-4">
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      ) : isError ? (
        <Card className="p-4 text-sm text-destructive">{errorMessage(error, t("errors.generic"))}</Card>
      ) : !data || data.competitors.length === 0 ? (
        <EmptyState icon={Eye} title={t("empty.title")} description={t("empty.description")} />
      ) : (
        <>
          <ComparisonTable rows={rows} days={days} brandId={brandId} canEdit={canEdit} />
          <TopPosts rows={data.competitors} days={days} />
        </>
      )}
    </div>
  );
}
